import { useEffect, useState } from 'react';

import { getClient } from 'lane-shared/apollo';
import { AdminUser } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { safeConvertToUUID, convertToUUID } from 'uuid-encoding';
import { hasPermission } from 'lane-shared/helpers';
import { PERMISSION_KEYS } from 'constants-permissions';
import { UserType } from 'lane-shared/types/User';

import { getServiceRequestFilterAttributesQuery } from 'graphql-queries';

interface IdNamePair {
  name: string;
  id: string;
}

type Props = {
  channelId: string;
  assignableTeams: AdminUser[];
  user: UserType | null;
  loadingTeams: boolean;
};

export type FilterParams = {
  company?: string;
  category?: string;
  status?: string;
  issue?: string;
  location?: string;
  isBillable?: boolean;
  isEscalated?: boolean;
  equipment?: string;
  assignees?: string;
  assigneeGroups?: string[];
  createDateStart?: Date;
  createDateEnd?: Date;
};

export type FilterOptions = {
  assignees: IdNamePair[];
  assigneeGroups: IdNamePair[];
  categories: string[];
  companies: IdNamePair[];
  issues: string[];
  equipment: IdNamePair[];
  locations: string[];
  statuses: string[];
};

export const useServiceRequestFilter = ({
  channelId,
  assignableTeams,
  user,
  loadingTeams,
}: Props) => {
  const [isAssignedView, setIsAssignedView] = useState(true);
  const [options, setOptions] = useState<FilterOptions>({
    assignees: [],
    assigneeGroups: [],
    categories: [],
    companies: [],
    issues: [],
    equipment: [],
    locations: [],
    statuses: [],
  });
  const [selectedParams, setSelectedParams] = useState<FilterParams>({});
  const [appliedParams, setAppliedParams] = useState<FilterParams>({});

  const canViewAllServiceRequests =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_WORK_ORDERS_SERVICE_REQUEST_VIEW],
      channelId,
      false
    );

  useEffect(() => {
    const init = async () => {
      await initFilters();
    };

    init().catch(console.error);
  }, [loadingTeams, channelId]);

  const initFilters = async () => {
    if (!channelId) return;

    const { data } = await getClient().query({
      query: getServiceRequestFilterAttributesQuery,
      variables: {
        channelIds: [channelId],
      },
      fetchPolicy: 'network-only',
    });

    let assignees = [];

    if (isAssignedView && !canViewAllServiceRequests) {
      assignees =
        data?.serviceRequestFilterAttributes?.assignees?.flatMap(
          (s: IdNamePair) => {
            if (s.id === safeConvertToUUID(user?._id)) {
              return { name: s.name, id: s.id };
            }

            return [];
          }
        ) || [];
    } else if (!canViewAllServiceRequests) {
      assignees = [];
    }

    let assigneeGroups: { name: string; id: string }[] = [];

    if (canViewAllServiceRequests) {
      assigneeGroups = assignableTeams
        ? assignableTeams.map(team => ({
            name: team.label,
            id: team.value,
          }))
        : [];
    } else if (!user?.roles) {
      assigneeGroups = [];
    } else {
      const userRole = user?.roles.filter(
        role =>
          role?.groupRole?.channel?._id === channelId &&
          role.groupRole.permissions.includes(
            PERMISSION_KEYS.PERMISSION_WORK_ORDERS_MODULE_ACCESS
          )
      );

      if (userRole.length) {
        const groupIDs = userRole.map((role: { groupRole: { _id: string } }) =>
          safeConvertToUUID(role?.groupRole?._id)
        );

        assigneeGroups = assignableTeams?.flatMap((s: AdminUser) => {
          if (groupIDs.includes(s.value)) {
            return {
              name: s.label,
              id: s.value,
            };
          }

          return [];
        });
      }
    }

    setOptions({
      ...data.serviceRequestFilterAttributes,
      assigneeGroups,
      ...(!canViewAllServiceRequests ? { assignees } : {}),
    });

    resetFilter();
  };

  const getUserGroupRoles = () => {
    const userRole = user?.roles.find(
      role =>
        role?.groupRole?.channel?._id === channelId &&
        role.groupRole.permissions.includes(
          PERMISSION_KEYS.PERMISSION_WORK_ORDERS_MODULE_ACCESS
        )
    );

    return userRole?.groupRole?._id
      ? [convertToUUID(userRole?.groupRole?._id)]
      : [];
  };

  const getAssigneeFilterOptions = () =>
    isAssignedView
      ? {
          assignees: convertToUUID(user?._id),
          assigneeGroups: [],
        }
      : {
          assignees: 'unassigned',
          assigneeGroups: getUserGroupRoles(),
        };

  const resetFilter = () => {
    setSelectedParams({
      ...(!canViewAllServiceRequests ? getAssigneeFilterOptions() : {}),
    });
    setAppliedParams({
      ...(!canViewAllServiceRequests ? getAssigneeFilterOptions() : {}),
    });
  };

  const closeFilter = () => {
    setSelectedParams(appliedParams);
  };

  const onFilterChange = (changes: FilterParams) => {
    setSelectedParams({ ...selectedParams, ...changes });
  };

  const applyFilter = () => {
    setAppliedParams(selectedParams);
  };

  useEffect(() => {
    setSelectedParams({
      ...selectedParams,
      ...(!canViewAllServiceRequests ? getAssigneeFilterOptions() : {}),
    });
    setAppliedParams({
      ...appliedParams,
      ...(!canViewAllServiceRequests ? getAssigneeFilterOptions() : {}),
    });
  }, [isAssignedView]);

  return {
    filterOptions: options,
    selectedParams,
    appliedParams,
    onFilterChange,
    resetFilter,
    closeFilter,
    applyFilter,
    isAssignedView,
    setIsAssignedView,
  };
};
